import type { ContactProfile } from '@chatbot/shared-types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { toast } from '../components/Toast.tsx';
import { ContactEditForm } from '../components/contacts/ContactEditForm.tsx';
import { MilestoneBadge, TemperatureBadge } from '../components/contacts/LifecycleBadges.tsx';
import { SourceChip } from '../components/contacts/SourceChip.tsx';
import { StateBadge } from '../components/conversations/StateBadge.tsx';
import { Button } from '../components/ui/index.ts';
import { contactsApi } from '../lib/contacts-api.ts';

export function ContactDetailPage() {
  const { tenantId, userId } = useParams<{ tenantId: string; userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ContactProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!tenantId || !userId) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    contactsApi
      .get(tenantId, decodeURIComponent(userId))
      .then((p) => {
        if (mounted) setProfile(p);
      })
      .catch((err: Error) => {
        if (mounted) {
          setError(err.message);
          toast.error(`Error al cargar el perfil: ${err.message}`);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [tenantId, userId]);

  const needsAttention =
    profile?.currentState === 'pending_human' || profile?.currentState === 'human_active';

  return (
    <AppShell>
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => navigate('/contacts')}
          className="self-start text-sm text-primary-600 hover:underline"
        >
          ← Volver a contactos
        </button>

        {loading ? (
          <div className="card p-6">
            <Skeleton rows={6} />
          </div>
        ) : error || !profile ? (
          <div className="card flex flex-col items-center justify-center gap-4 py-16 text-center">
            <p className="text-danger-600 font-semibold">
              {error ?? 'No se pudo cargar el perfil del contacto.'}
            </p>
          </div>
        ) : (
          <>
            <section className="card p-6 flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <Avatar
                  name={
                    profile.identity?.displayName ??
                    profile.userDisplayName ??
                    profile.userIdentifier ??
                    profile.userId
                  }
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-content-primary truncate">
                      {profile.identity?.displayName ||
                        profile.userDisplayName ||
                        profile.userIdentifier ||
                        profile.userId}
                    </h1>
                    {profile.identity && <SourceChip source={profile.identity.displayNameSource} />}
                    <MilestoneBadge
                      milestone={profile.milestone ?? null}
                      label={profile.milestoneLabel ?? null}
                    />
                    <TemperatureBadge temperature={profile.temperature} />
                    {profile.channel && (
                      <span className="text-xs px-2 py-0.5 rounded bg-surface-sunken text-content-secondary font-medium uppercase font-mono">
                        {profile.channel}
                      </span>
                    )}
                  </div>
                  {profile.userIdentifier && (
                    <p className="text-sm text-content-tertiary font-mono">
                      {profile.userIdentifier}
                    </p>
                  )}
                  <p className="text-xs text-content-tertiary dark:text-content-tertiary font-mono mt-1">
                    ID interno: {profile.userId}
                  </p>
                </div>
                {!editing && profile.identity && (
                  <Button
                    id="contact-edit"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(true)}
                  >
                    Editar
                  </Button>
                )}
                {needsAttention && (
                  <span className="px-3 py-1 rounded-full bg-warning-100 dark:bg-warning-900/40 text-warning-700 dark:text-warning-300 font-semibold text-sm shadow-sm">
                    ⚠️ Atención requerida
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-edge-subtle">
                <Stat label="Estado actual">
                  {profile.currentState ? <StateBadge state={profile.currentState} /> : '—'}
                </Stat>
                <Stat label="Asignado a">{profile.assignedAdminName ?? '—'}</Stat>
                <Stat label="Última actividad">{formatAbs(profile.lastActivity)}</Stat>
                <Stat label="Último mensaje">
                  {profile.lastMessageAt ? formatAbs(profile.lastMessageAt) : '—'}
                </Stat>
              </div>
            </section>

            {editing && profile.identity && tenantId && userId && (
              <section className="card p-6">
                <h2 className="text-lg font-semibold text-content-primary mb-4">Editar contacto</h2>
                <ContactEditForm
                  identity={profile.identity}
                  notes={profile.notes ?? null}
                  onCancel={() => setEditing(false)}
                  onSave={async (body) => {
                    const res = await contactsApi.update(
                      tenantId,
                      decodeURIComponent(userId),
                      body,
                    );
                    setProfile((p) => (p ? { ...p, identity: res.identity, notes: res.notes } : p));
                    setEditing(false);
                  }}
                />
              </section>
            )}

            <section className="card p-6">
              <h2 className="text-lg font-semibold text-content-primary mb-3">Conversaciones</h2>
              {profile.conversations.length === 0 ? (
                <p className="text-sm text-content-tertiary">
                  Este contacto aún no tiene conversaciones registradas.
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {profile.conversations.map((conv, idx) => (
                    <li
                      key={`${conv.startedAt}-${idx}`}
                      className="py-3 flex items-center gap-3 hover:bg-surface-sunken dark:hover:bg-neutral-850 px-2 -mx-2 rounded cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/conversations/${profile.tenantId}/${encodeURIComponent(profile.userId)}`,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          navigate(
                            `/conversations/${profile.tenantId}/${encodeURIComponent(profile.userId)}`,
                          );
                        }
                      }}
                    >
                      <StateBadge state={conv.state} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-content-primary">
                          Iniciada: {formatAbs(conv.startedAt)}
                          {conv.endedAt ? ` · Cerrada: ${formatAbs(conv.endedAt)}` : ' · Activa'}
                        </p>
                        <p className="text-xs text-content-tertiary">
                          {conv.messagesCount} mensaje{conv.messagesCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span className="text-sm text-primary-600 hover:underline">Ver chat →</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card p-6">
              <h2 className="text-lg font-semibold text-content-primary mb-3">Últimos mensajes</h2>
              {profile.recentMessages.length === 0 ? (
                <p className="text-sm text-content-tertiary">Sin mensajes recientes.</p>
              ) : (
                <ul className="space-y-3">
                  {profile.recentMessages.map((msg, idx) => (
                    <li
                      key={`${msg.timestamp}-${idx}`}
                      className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          msg.role === 'user'
                            ? 'bg-surface-sunken text-content-primary'
                            : msg.role === 'admin'
                              ? 'bg-warning-100 dark:bg-warning-900/40 text-warning-900 dark:text-warning-100'
                              : 'bg-primary-100 dark:bg-primary-900/40 text-primary-900 dark:text-primary-100'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        <p className="text-xs text-content-tertiary font-mono mt-1">
                          {msg.role} · {formatAbs(msg.timestamp)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-content-tertiary mb-1">{label}</p>
      <div className="text-sm text-content-primary">{children}</div>
    </div>
  );
}

function Avatar({ name, size = 'md' }: { name: string; size?: 'md' | 'lg' }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  const cls = size === 'lg' ? 'h-16 w-16 text-lg shrink-0' : 'h-10 w-10 text-sm shrink-0';
  return (
    <div
      className={`${cls} rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center font-semibold`}
    >
      {initials || '?'}
    </div>
  );
}

function formatAbs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
